<template>
  <div class="kv-ve__group">
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-row-columns") }}</label>
      <KvPopup placement="bottom left" max-width="320px" min-width="320px">
        <template #trigger="{ toggle }">
          <button type="button" class="form-control kv-ve__columns-trigger" @click.stop="toggle()">
            <span class="kv-ve__columns-trigger-left">
              {{ row.columns.length }}
              <span class="kv-ve__columns-trigger-suffix">
                {{
                  row.columns.length === 1
                    ? $_("visualEditor-emailEditor-row-columnSingular")
                    : $_("visualEditor-emailEditor-row-columnPlural")
                }}
              </span>
            </span>
            <span class="kv-ve__columns-trigger-caret">▾</span>
          </button>
        </template>

        <template #content="{ toggle }">
          <span class="kv-popup-title">{{ $_("visualEditor-emailEditor-row-columns") }}</span>
          <div class="kv-ve__columns-options">
            <button
              v-for="opt in rowPalette"
              :key="opt.key"
              type="button"
              class="kv-ve__columns-option"
              :class="{ active: isRowLayoutActive(row, opt) }"
              @click="setRowColumns(row, opt.columns, opt.weights); toggle(false)"
            >
              <div class="kv-ve__columns-preview" :style="rowPalettePreviewStyle(opt)" aria-hidden="true">
                <div v-for="i in opt.columns" :key="i" class="kv-ve__columns-col"></div>
              </div>
              <div class="kv-ve__columns-label">{{ opt.caption }}</div>
            </button>
          </div>
        </template>
      </KvPopup>
    </div>

    <div v-if="row.columns.length > 1" class="form-group">
      <label>{{ $_("visualEditor-emailEditor-row-mobileOrientation-label") }}</label>
      <div class="kv-ve__mobile-orientation">
        <KvTooltip :title="$_('visualEditor-emailEditor-row-mobileOrientation-leftOnTop')">
          <button
            type="button"
            class="kv-ve__mobile-orientation-btn"
            :class="{ active: mobileOrientation === 'stack' }"
            :title="$_('visualEditor-emailEditor-row-mobileOrientation-leftOnTop')"
            :aria-label="$_('visualEditor-emailEditor-row-mobileOrientation-leftOnTop')"
            @click="mobileOrientation = 'stack'"
          >
            <span class="kv-ve__ori-icon kv-ve__ori-icon--stack" aria-hidden="true">
              <!-- top-left: two filled squares -->
              <span class="kv-ve__sq kv-ve__sq--filled kv-ve__sq--a"></span>
              <span class="kv-ve__sq kv-ve__sq--filled kv-ve__sq--b"></span>
              <!-- ghost below left + arrow to the right (points right) -->
              <span class="kv-ve__sq kv-ve__sq--ghost kv-ve__sq--g"></span>
              <span class="kv-ve__arrow kv-ve__arrow--left" aria-hidden="true">↵</span>
            </span>
          </button>
        </KvTooltip>
        <KvTooltip :title="$_('visualEditor-emailEditor-row-mobileOrientation-rightOnTop')">
          <button
            type="button"
            class="kv-ve__mobile-orientation-btn"
            :class="{ active: mobileOrientation === 'stack-reverse' }"
            :title="$_('visualEditor-emailEditor-row-mobileOrientation-rightOnTop')"
            :aria-label="$_('visualEditor-emailEditor-row-mobileOrientation-rightOnTop')"
            @click="mobileOrientation = 'stack-reverse'"
          >
            <span class="kv-ve__ori-icon kv-ve__ori-icon--stack" aria-hidden="true">
              <span class="kv-ve__sq kv-ve__sq--filled kv-ve__sq--a"></span>
              <span class="kv-ve__sq kv-ve__sq--filled kv-ve__sq--b"></span>
              <span class="kv-ve__arrow kv-ve__arrow--right" aria-hidden="true">↵</span>
              <span class="kv-ve__sq kv-ve__sq--ghost kv-ve__sq--g"></span>

      
            </span>
          </button>
        </KvTooltip>
        <KvTooltip :title="$_('visualEditor-emailEditor-row-mobileOrientation-keepColumns')">
          <button
            type="button"
            class="kv-ve__mobile-orientation-btn"
            :class="{ active: mobileOrientation === 'keep' }"
            :title="$_('visualEditor-emailEditor-row-mobileOrientation-keepColumns')"
            :aria-label="$_('visualEditor-emailEditor-row-mobileOrientation-keepColumns')"
            @click="mobileOrientation = 'keep'"
          >
            <span class="kv-ve__ori-icon kv-ve__ori-icon--keep" aria-hidden="true">
              <!-- top-center: two filled squares (no ghost, no arrow) -->
              <span class="kv-ve__sq kv-ve__sq--filled kv-ve__sq--a"></span>
              <span class="kv-ve__sq kv-ve__sq--filled kv-ve__sq--b"></span>
            </span>
          </button>
        </KvTooltip>
      </div>
    </div>

    <div class="form-group">
      <label>{{ $_('visualEditor-emailEditor-common-margin') }}</label>
      <VeSpacingPicker
        v-model="marginProxy"
        :y-label="$_('visualEditor-emailEditor-common-axisY')"
        :x-label="$_('visualEditor-emailEditor-common-axisX')"
        :centerLabel="$_('visualEditor-emailEditor-common-margin')"
        :min="0"
        :max="200"
        :step="1"
      />
      <small class="text-muted d-block mt-1">{{ $_("visualEditor-emailEditor-row-marginHelp") }}</small>
    </div>

    <div class="form-group">
      <label>{{ $_('visualEditor-emailEditor-common-padding') }}</label>
      <VeSpacingPicker
        v-model="paddingProxy"
        :y-label="$_('visualEditor-emailEditor-common-axisY')"
        :x-label="$_('visualEditor-emailEditor-common-axisX')"
        :centerLabel="$_('visualEditor-emailEditor-common-padding')"
        :min="0"
        :max="200"
        :step="1"
      />
    </div>

    <div class="form-group">
      <VeBorderSettings
        v-model="borderProxy"
        :label="$_('visualEditor-emailEditor-borderSettings-label')"
      />
    </div>

    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-common-corners") }}</label>
      <VeCornersPicker
        v-model="borderRadiusProxy"
        :options="borderRadiusOptions.map((o) => ({ value: o.value, title: $_(o.labelKey) }))"
      />
    </div>
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-row-columnGap") }}</label>
      <input class="form-control" type="number" v-model.number="row.settings.columnGap" min="0" />
    </div>
    <div class="form-group">
      <VeBackgroundPicker
        v-model="backgroundPickerProxy"
        :label="$_('visualEditor-emailEditor-row-background')"
        :colorLabel="$_('visualEditor-emailEditor-backgroundPicker-color')"
        :imageLabel="$_('visualEditor-emailEditor-backgroundPicker-image')"
        :chooseImageLabel="$_('visualEditor-emailEditor-backgroundPicker-choose')"
        :removeImageLabel="$_('visualEditor-emailEditor-backgroundPicker-remove')"
        :emptyImageLabel="$_('visualEditor-emailEditor-backgroundPicker-empty')"
      />
      <small class="text-muted d-block mt-1">{{ $_("visualEditor-emailEditor-row-backgroundHelp") }}</small>
    </div>
  </div>
</template>

<script>
import KvPopup from "@/components/ui/kv-popup.vue";
import VeBackgroundPicker from "../components/VeBackgroundPicker.vue";
import VeSpacingPicker from "../components/VeSpacingPicker.vue";
import VeBorderSettings from "../components/VeBorderSettings.vue";
import VeCornersPicker from "../components/VeCornersPicker.vue";
import KvTooltip from "@/components/ui/kv-tooltip.vue";
import { kvVeId } from "../lib/id";

export default {
  name: "VeRowSettings",
  components: { KvPopup, VeBackgroundPicker, VeSpacingPicker, VeBorderSettings, VeCornersPicker },
  props: {
    row: { type: Object, required: true },
    rowPalette: { type: Array, required: true },
    borderRadiusOptions: { type: Array, required: true },
  },
  computed: {
    mobileOrientation: {
      get() {
        return this.row?.settings?.mobileOrientation || "stack";
      },
      set(v) {
        if (!this.row.settings) this.$set(this.row, "settings", {});
        const next = v === "keep" || v === "stack-reverse" ? v : "stack";
        this.$set(this.row.settings, "mobileOrientation", next);
      },
    },
    backgroundPickerProxy: {
      get() {
        const s = this.row?.settings || {};
        const mode = String(s.backgroundMode || "").toLowerCase() === "image" ? "image" : "color";
        const color = s.backgroundColor || "#ffffff";
        const imageUrl = s.backgroundImageUrl || null;
        const inferredMode = imageUrl ? "image" : mode;
        return {
          mode: inferredMode,
          color,
          imageUrl,
          sizeMode: s.backgroundSizeMode || "cover",
          sizeUnit: s.backgroundSizeUnit || "pct",
          sizePx: s.backgroundSizePx,
          sizePct: s.backgroundSizePct,
          repeat: s.backgroundRepeat || "no-repeat",
          position: s.backgroundPosition || "center center",
        };
      },
      set(next) {
        const v = next || {};
        const mode = String(v.mode || "color").toLowerCase() === "image" ? "image" : "color";
        const color = typeof v.color === "string" && v.color ? v.color : "#ffffff";
        const imageUrl = typeof v.imageUrl === "string" && v.imageUrl ? v.imageUrl : null;
        if (!this.row.settings) this.$set(this.row, "settings", {});
        this.$set(this.row.settings, "backgroundMode", mode);
        this.$set(this.row.settings, "backgroundColor", color === "#ffffff" ? null : color);
        this.$set(this.row.settings, "backgroundImageUrl", imageUrl);

        // Extra image controls (size/repeat/position)
        const sizeModeRaw = String(v.sizeMode || "").toLowerCase();
        const sizeMode = sizeModeRaw === "contain" || sizeModeRaw === "custom" ? sizeModeRaw : "cover";
        const sizeUnitRaw = String(v.sizeUnit || "").toLowerCase();
        const sizeUnit = sizeUnitRaw === "px" ? "px" : "pct";
        const px = Number(v.sizePx);
        const sizePx = Number.isFinite(px) ? Math.min(2000, Math.max(100, Math.round(px))) : 600;
        const pct = Number(v.sizePct);
        const sizePct = Number.isFinite(pct) ? Math.min(100, Math.max(0, Math.round(pct))) : 100;
        const repeatRaw = String(v.repeat || "").toLowerCase();
        const repeat =
          repeatRaw === "repeat" || repeatRaw === "repeat-x" || repeatRaw === "repeat-y"
            ? repeatRaw
            : "no-repeat";
        const position = String(v.position || "center center");

        this.$set(this.row.settings, "backgroundSizeMode", sizeMode);
        this.$set(this.row.settings, "backgroundSizeUnit", sizeUnit);
        this.$set(this.row.settings, "backgroundSizePx", sizePx);
        this.$set(this.row.settings, "backgroundSizePct", sizePct);
        this.$set(this.row.settings, "backgroundRepeat", repeat);
        this.$set(this.row.settings, "backgroundPosition", position);
      },
    },
    marginProxy: {
      get() {
        const s = this.row?.settings || {};
        const safe = (n) => {
          const x = Number(n);
          return Number.isFinite(x) && x >= 0 ? Math.round(x) : 0;
        };
        return {
          top: safe(s.marginTop),
          right: safe(s.marginRight),
          bottom: safe(s.marginBottom),
          left: safe(s.marginLeft),
        };
      },
      set(next) {
        const v = next || {};
        const safe = (n) => {
          const x = Number(n);
          return Number.isFinite(x) ? Math.max(0, Math.round(x)) : 0;
        };
        if (!this.row.settings) this.$set(this.row, "settings", {});
        this.$set(this.row.settings, "marginTop", safe(v.top));
        this.$set(this.row.settings, "marginRight", safe(v.right));
        this.$set(this.row.settings, "marginBottom", safe(v.bottom));
        this.$set(this.row.settings, "marginLeft", safe(v.left));
      },
    },
    paddingProxy: {
      get() {
        const s = this.row?.settings || {};
        const safe = (n) => {
          const x = Number(n);
          return Number.isFinite(x) && x >= 0 ? Math.round(x) : 8;
        };
        return {
          top: safe(s.paddingTop),
          right: safe(s.paddingRight),
          bottom: safe(s.paddingBottom),
          left: safe(s.paddingLeft),
        };
      },
      set(next) {
        const v = next || {};
        const safe = (n) => {
          const x = Number(n);
          return Number.isFinite(x) ? Math.max(0, Math.round(x)) : 0;
        };
        if (!this.row.settings) this.$set(this.row, "settings", {});
        this.$set(this.row.settings, "paddingTop", safe(v.top));
        this.$set(this.row.settings, "paddingRight", safe(v.right));
        this.$set(this.row.settings, "paddingBottom", safe(v.bottom));
        this.$set(this.row.settings, "paddingLeft", safe(v.left));
      },
    },
    borderProxy: {
      get() {
        const b = this.row?.settings?.border || {};
        const sides = b.sides || {};
        return {
          enabled: Boolean(b.enabled),
          style: b.style || "solid",
          color: b.color || "#e5e7eb",
          width: Number.isFinite(Number(b.width)) ? Math.max(1, Math.min(20, Math.round(Number(b.width)))) : 1,
          sides: {
            top: sides.top !== false,
            right: sides.right !== false,
            bottom: sides.bottom !== false,
            left: sides.left !== false,
          },
        };
      },
      set(next) {
        const v = next || {};
        if (!this.row.settings) this.$set(this.row, "settings", {});
        if (!this.row.settings.border) this.$set(this.row.settings, "border", {});
        this.$set(this.row.settings.border, "enabled", Boolean(v.enabled));
        this.$set(this.row.settings.border, "style", String(v.style || "solid").toLowerCase());
        this.$set(this.row.settings.border, "color", typeof v.color === "string" && v.color ? v.color : "#e5e7eb");
        const w = Number(v.width);
        const width = Number.isFinite(w) ? Math.max(1, Math.min(20, Math.round(w))) : 1;
        this.$set(this.row.settings.border, "width", width);
        const sides = v.sides || {};
        this.$set(this.row.settings.border, "sides", {
          top: sides.top !== false,
          right: sides.right !== false,
          bottom: sides.bottom !== false,
          left: sides.left !== false,
        });
      },
    },
    borderRadiusProxy: {
      get() {
        const val = this.row?.settings?.borderRadius;
        if (val && typeof val === "object" && !Array.isArray(val)) {
          return val;
        }
        const n = Number(val ?? 0);
        return Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
      },
      set(v) {
        if (!this.row.settings) this.$set(this.row, "settings", {});
        if (v && typeof v === "object" && !Array.isArray(v) && v.topLeft !== undefined) {
          this.$set(this.row.settings, "borderRadius", v);
        } else {
          const n = Number(v);
          const next = Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
          if (!next) this.$delete(this.row.settings, "borderRadius");
          else this.$set(this.row.settings, "borderRadius", next);
        }
      },
    },
  },
  mounted() {
    this.normalizeRowWeights(this.row);
  },
  methods: {
    rowPalettePreviewStyle(item) {
      const w = Array.isArray(item?.weights) ? item.weights : [];
      if (w.length >= 2) {
        return { gridTemplateColumns: w.map((x) => `${Number(x) || 1}fr`).join(" ") };
      }
      return { "--kv-ve-cols": item.columns };
    },
    getRowWeights(row) {
      const cols = row?.columns || [];
      const n = Math.max(1, cols.length);
      const raw = row?.settings?.columnWeights;
      return Array.from({ length: n }, (_, i) => {
        const v = Number(raw?.[i] ?? 1);
        return Number.isFinite(v) && v > 0 ? v : 1;
      });
    },
    normalizeRowWeights(row) {
      if (!row) return;
      const cols = row?.columns || [];
      const n = Math.max(1, cols.length);
      const raw = row?.settings?.columnWeights;
      const next = Array.from({ length: n }, (_, i) => {
        const v = Number(raw?.[i] ?? 1);
        return Number.isFinite(v) && v > 0 ? v : 1;
      });
      if (!row.settings) this.$set(row, "settings", {});
      const cur = row.settings.columnWeights;
      const same =
        Array.isArray(cur) &&
        cur.length === next.length &&
        cur.every((x, i) => Number(x) === Number(next[i]));
      if (!same) this.$set(row.settings, "columnWeights", next);
    },
    isRowLayoutActive(row, opt) {
      const n = row?.columns?.length || 0;
      if (n !== opt.columns) return false;
      const w = this.getRowWeights(row);
      const ow = Array.isArray(opt.weights) ? opt.weights : Array.from({ length: opt.columns }, () => 1);
      if (ow.length !== w.length) return false;
      for (let i = 0; i < w.length; i += 1) {
        if (Number(w[i]) !== Number(ow[i])) return false;
      }
      return true;
    },
    setRowColumns(row, nextCount, nextWeights = null) {
      if (!row) return;
      if (!row.columns) this.$set(row, "columns", []);

      const n = Math.max(1, Math.min(4, Number(nextCount) || 1));
      const current = row.columns?.length || 0;

      if (row.kind === "section") {
        this.$set(row, "kind", "custom");
        this.$set(row, "preset", null);
      }

      if (n > current) {
        for (let i = current; i < n; i += 1) {
          row.columns.push({ id: kvVeId("col"), blocks: [] });
        }
      } else if (n < current) {
        const kept = row.columns.slice(0, n);
        const last = kept[kept.length - 1];
        const removed = row.columns.slice(n);
        removed.forEach((c) => {
          (c.blocks || []).forEach((b) => last.blocks.push(b));
        });
        row.columns = kept;
      }

      if (!row.settings) this.$set(row, "settings", {});
      const weights = Array.isArray(nextWeights)
        ? nextWeights
        : Array.isArray(row.settings.columnWeights)
          ? row.settings.columnWeights
          : null;
      const normalized = Array.from({ length: n }, (_, i) => {
        const v = Number(weights?.[i] ?? 1);
        return Number.isFinite(v) && v > 0 ? v : 1;
      });
      this.$set(row.settings, "columnWeights", normalized);
      this.normalizeRowWeights(row);
    },
  },
};
</script>

<style scoped lang="less">
.kv-ve__mobile-orientation {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.kv-ve__mobile-orientation-btn {
  all: unset;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  transition: box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}
.kv-ve__mobile-orientation-btn:hover {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.12);
}
.kv-ve__mobile-orientation-btn.active {
  border-color: rgba(42, 170, 76, 0.85);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.16);
  background: rgba(42, 170, 76, 0.06);
}

.kv-ve__ori-icon {
  position: relative;
  width: 40px;
  height: 40px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 1px;
}

.kv-ve__sq {
  border-radius: 2px;
}

.kv-ve__sq--filled {
  background: #111827;
}

.kv-ve__sq--ghost {
  border: 1px dashed rgba(17, 24, 39, 0.55);
  background: rgba(17, 24, 39, 0.08);
}

/* Arrow (placed as a grid cell) */
.kv-ve__arrow {
  place-self: center;
  color: #111827;
  font-size: 10px;
  line-height: 1;
  pointer-events: none;
  font-weight: bold;
  z-index: 1;
  &.kv-ve__arrow--right {
    transform: scaleX(-1);
  }
}
</style>


